import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  getPersonReturn,
  clearPersonReturn,
  subscribeToPersonReturn,
} from "../../utils/personReturn";
import "./PersonReturnPill.css";

/**
 * Mounted once for the whole app so no destination page has to know about it. It
 * only shows on the exact route the reader was sent to from a person profile,
 * which keeps it from following them around the rest of the site.
 */
export default function PersonReturnPill() {
  const location = useLocation();
  const { pathname } = location;
  const [entry, setEntry] = useState(null);

  const sync = useCallback(() => {
    // Back on a profile — the round trip is over.
    if (pathname.startsWith("/people/")) {
      clearPersonReturn();
      setEntry(null);
      return;
    }

    // Router state is the fast path; storage covers a refresh on the destination.
    const stateEntry = location.state?.personReturn;
    const saved =
      stateEntry?.personId && stateEntry?.route ? stateEntry : getPersonReturn();

    setEntry(saved && saved.route === pathname ? saved : null);
  }, [pathname, location.state]);

  useEffect(() => {
    sync();
  }, [sync]);

  // A destination page can re-stamp the hop after this effect has already run
  // (battle trails do, on every prev / next / related hop).
  useEffect(() => subscribeToPersonReturn(sync), [sync]);

  if (!entry) return null;

  const name = entry.personName || "person profile";

  return (
    <div className="ctb-returnPill" data-testid="person-return-pill">
      <Link
        className="ctb-returnPillLink"
        to={`/people/${entry.personId}`}
        onClick={clearPersonReturn}
      >
        <span className="ctb-returnPillArrow" aria-hidden="true">
          ←
        </span>
        <span className="ctb-returnPillCopy">
          <span className="ctb-returnPillEyebrow">Back to profile</span>
          <span className="ctb-returnPillName">{name}</span>
        </span>
      </Link>

      <button
        type="button"
        className="ctb-returnPillDismiss"
        aria-label={`Dismiss return link to ${name}`}
        onClick={() => {
          clearPersonReturn();
          setEntry(null);
        }}
      >
        ×
      </button>
    </div>
  );
}
